import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Drawer, Form, Input, Select, message, Space, Tooltip } from "antd";
import axios from "axios";
import { SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import constants from "../../constant";

const { Option } = Select;

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [systemUsers, setSystemUsers] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 20,
      position: ["topCenter"],
    },
    filters: {},
  });
  const searchInput = useRef(null);
  const [form] = Form.useForm();

  // Fetch products with optional filtering
  const fetchProducts = async () => {
    try {
      let query = [];
      Object.keys(tableParams.filters).forEach((k) => {
        if (tableParams.filters[k]?.length > 0) {
          query.push(`${k}:sw=${tableParams.filters[k]}`);
        }
      });

      const res = await axios.get(`${constants.DOMAIN}/api/v1/products/?${query.join("&")}`);
      setProducts(res.data.data.products);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: res.data.data.products.length,
        },
      })
    } catch (error) {
      console.error("Failed to fetch products:", error);
      message.error("Failed to load products");
    }
  };

  const fetchSystemUsers = async () => {
    try {
      const response = await axios.get(`${constants.DOMAIN}/api/v1/systemuser/getUser`);
      setSystemUsers(response.data.data.users);
    } catch (error) {
      message.error('Failed to fetch system users');
    }
  };
  useEffect(() => {
    fetchSystemUsers();
  }, []);
  // Fetch products when filters change
  useEffect(() => {
    fetchProducts();
  }, [tableParams.filters]);

  console.log(tableParams);
  
  // Update form fields when currentProduct changes
  useEffect(() => {
    if (currentProduct) {
      form.setFieldsValue({
        code: currentProduct.code,
        name: currentProduct.name,
        poc: currentProduct.POC,
      });
    } else {
      form.resetFields(); // Reset form when no current product
    }
  }, [currentProduct, form]);

  // Open drawer for editing a product
  const handleEdit = (product) => {
    setCurrentProduct(product);
    setIsDrawerOpen(true);
  };

  // Delete a product
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${constants.DOMAIN}/api/v1/products/${id}`);
      message.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      console.error("Failed to delete product:", error);
      message.error("Failed to delete product");
    }
  };

  // Submit form (create or update product)
  const handleFormSubmit = async (values) => {
    try {
      if (currentProduct) {
        // Update existing product
        await axios.post(`${constants.DOMAIN}/api/v1/products/`, values);
        message.success("Product updated successfully");
      } else {
        // Create new product
        await axios.post(`${constants.DOMAIN}/api/v1/products/`, values);
        message.success("Product created successfully");
      }
      setIsDrawerOpen(false);
      fetchProducts();
      form.resetFields();
      setCurrentProduct(null);
    } catch (error) {
      console.error("Full error:", error);
      message.error("Failed to save product");
    }
  };

  // Search functionality for columns
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={close}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  // Handle search action
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setTableParams({
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1 },
      filters: { [dataIndex]: selectedKeys },
    });
  };

  // Reset search filters
  const handleReset = (clearFilters) => {
    clearFilters();
    fetchProducts();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between mb-4">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setCurrentProduct(null);
            setIsDrawerOpen(true);
          }}
        >
          Add Product
        </Button>
      </div>

      <Table
        dataSource={products}
        rowKey="_id"
        pagination={{
          ...tableParams.pagination,
          responsive: true,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        columns={[
          { title: "Code", dataIndex: "code", ...getColumnSearchProps("code") },
          { title: "Name", dataIndex: "name", ...getColumnSearchProps("name") },
          {
            title: "Actions",
            render: (text, record) => (
              <Space size="middle">
                <Tooltip title="Edit">
                  <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                </Tooltip>
                <Tooltip title="Delete">
                  <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                </Tooltip>
                <Tooltip title="Preview">
                  <Button icon={<EyeOutlined />} onClick={() => message.info(JSON.stringify(record, null, 2))} />
                </Tooltip>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title={currentProduct ? "Edit Product" : "Add Product"}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setCurrentProduct(null);
          form.resetFields();
        }}
        width={400}
      >
        <Form
          form={form}
          initialValues={currentProduct || { code: '', name: '', poc: [] }}
          onFinish={handleFormSubmit}
          layout="vertical"
        >
          <Form.Item
            name="code"
            label="Product Code"
            rules={[{ required: true, message: "Please input the product code!", }]}
          >
            <Input placeholder="Enter product code" disabled={currentProduct ? true : false}/>
          </Form.Item>

          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: "Please input the product name!" }]}
          >
            <Input placeholder="Enter product name" />
          </Form.Item>

          <Form.Item name="POC" label="Point of Contact">
            <Select mode="multiple" placeholder="Select POC">
            {systemUsers.map(user => (
                    <Option key={user._id} value={user._id}>
                      {user.name}
                    </Option>
                  ))}
            </Select>
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            Save
          </Button>
        </Form>
      </Drawer>
    </div>
  );
};

export default ProductManagement;
